// packages/frontend/src/features/imports/duplicates.ts
//
// Duplicate-import detection (Plan 12, Task 9). Couple-scale — the household's
// whole library comfortably fits a single `toArray()` scan, so no index/GSI
// equivalent is needed on the Dexie side.
import type { RecipeApiItem } from "@recipevault/shared";
import { db } from "../../sync/db.js";

// Tracking/share-artifact query params that don't change what the page is —
// stripped so the same recipe shared twice (e.g. once from a newsletter link,
// once from an Instagram share) still normalizes to the same key.
const STRIP_QUERY_PREFIXES = ["utm_"];
const STRIP_QUERY_EXACT = new Set(["fbclid", "igsh"]);

/**
 * Canonicalizes a recipe URL for duplicate comparison: lowercases the host,
 * drops the hash, strips tracking query params, and trims a trailing slash
 * from the path. Returns null if the URL can't be parsed.
 */
export function normalizeRecipeUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const params = new URLSearchParams(parsed.search);
  for (const key of Array.from(params.keys())) {
    if (STRIP_QUERY_EXACT.has(key) || STRIP_QUERY_PREFIXES.some((p) => key.startsWith(p))) {
      params.delete(key);
    }
  }
  const search = params.toString();

  const host = parsed.hostname.toLowerCase();
  const port = parsed.port ? `:${parsed.port}` : "";
  let path = parsed.pathname;
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }

  return `${parsed.protocol}//${host}${port}${path}${search ? `?${search}` : ""}`;
}

/**
 * Scans the local Dexie `recipes` cache for an active (non-soft-deleted)
 * recipe whose `source.url` normalizes to the same key as `url`. Returns the
 * first match or null. `source.url` is optional on `RecipeApiItem` (manual
 * recipes, text-paste imports) — rows without it are skipped.
 */
export async function findDuplicateByUrl(url: string): Promise<RecipeApiItem | null> {
  const normalized = normalizeRecipeUrl(url);
  if (normalized === null) return null;

  const all = await db.recipes.toArray();
  for (const r of all) {
    if (r.deletedAt) continue;
    const candidateUrl = r.source?.url;
    if (!candidateUrl) continue;
    if (normalizeRecipeUrl(candidateUrl) === normalized) return r;
  }
  return null;
}
